import {
  Box,
  InputAdornment,
} from '@mui/material';
import { memo } from 'react';
import { Controller } from 'react-hook-form';

import { FormControlLabel } from '../FormControlLabel/index.js';
import { Input } from '../Input/index.js';
import type { FormInputProps } from './FormInput.types.js';

const toNumber = (value: string, { decimalPoints }: { decimalPoints?: number } = {}) => {
  if (value === '') {
    return undefined;
  }

  const splitted = value.split('.');

  const output = (() => {
    if (splitted.length > 1 && decimalPoints) {
      return parseFloat(`${splitted[0]}.${splitted[1].slice(0, decimalPoints)}`);
    } else {
      return parseFloat(value);
    }
  })();

  return isNaN(output) ? 0 : output;
};

const INPUT_CHARACTERS_LIMIT = 99;

export const FormInput = memo(
  ({
    control,
    field,
    disabled = field.disabled ?? false,
  }: FormInputProps) => {
    return (
      <Controller
        key={`${field.name}`}
        name={field.name}
        control={control}
        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
          <Box {...field?.wrapperProps}>
            <FormControlLabel
              label={field?.label ?? ''}
              labelPlacement="top"
              control={(
                <Input
                  fullWidth
                  key={field.name}
                  value={value}
                  margin="none"
                  type={field.type ?? 'text'}
                  inputRef={ref}
                  error={Boolean(error)}
                  helperText={error?.message}
                  required={field.required}
                  onChange={(e) => {
                    if (field.type === 'number') {
                      onChange(toNumber(e.target.value, { decimalPoints: field.componentProps?.decimalPoints }));
                    } else {
                      // We convert empty string to undefined, because that will clear validation in case of something like z.string().min(1).optional()
                      // empty string would trigger .min(1)
                      // and undefined falls into optional and therefore is correct
                      onChange(e.target.value === '' ? undefined : e.target.value);
                    }
                  }}
                  onBlur={onBlur}
                  variant="outlined"
                  data-testid={`input-${field.name}`}
                  disabled={
                    disabled || field?.inputProps?.disabled || field?.disabled
                  }
                  slotProps={{
                    htmlInput: {
                      ...field.inputProps,
                      step: 'any',
                      placeholder: field?.placeholder ?? '',
                      maxLength: INPUT_CHARACTERS_LIMIT,
                    },
                    input: {
                      startAdornment: field?.startAdornment && (
                        <InputAdornment position="start">
                          {field.startAdornment}
                        </InputAdornment>
                      ),
                      endAdornment: field?.endAdornment && (
                        <InputAdornment position="end">
                          {field?.endAdornment}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>
        )}
      />
    );
  },
);

FormInput.displayName = 'FormInput';
